import React from 'react'
import { ClassNames } from '@emotion/react'
import {
  Box,
  Color,
  Display,
  Modal,
  ModalBody,
  ModalClose,
  ModalHeader,
  Heading3,
} from '@gousto-internal/citrus-react'

import { DeliveryPanel } from './components/DeliveryPanel'
import { PortionSizePanel } from './components/PortionSizePanel'
import { PostcodePanel } from './components/PostcodePanel'
import { SummaryPanel } from './components/SummaryPanel'
import type { LoggedOutBoxSummaryController, LoggedOutBoxSummaryModalProps } from './types'

const MODAL_NAME = 'logged-out-box-summary-modal'

const renderStep = ({ step, views, actions }: LoggedOutBoxSummaryController) => {
  switch (step) {
    case 'postcode':
      return <PostcodePanel view={views.postcode} actions={actions} />
    case 'delivery':
      return <DeliveryPanel view={views.delivery} actions={actions} />
    case 'portionSize':
      return <PortionSizePanel view={views.portionSize} actions={actions} />
    case 'summary':
      return <SummaryPanel view={views.summary} actions={actions} />
    default:
      // Exhaustiveness guard: if a new step is added to LoggedOutBoxSummaryStep
      // without a matching panel, this line fails to compile.
      return ((exhaustive: never) => exhaustive)(step)
  }
}

const LoggedOutBoxSummaryModalComponent = ({
  controller,
  recipeDetail,
}: LoggedOutBoxSummaryModalProps) => {
  if (!controller.isOpen) {
    return null
  }

  const activeView = controller.views[controller.step]

  return (
    <ClassNames>
      {({ css }) => (
        <Modal
          name={MODAL_NAME}
          isOpen={controller.isOpen}
          onRequestClose={controller.actions.onClose}
          contentLabel="Box summary"
          // The menu's box-summary bar sits in a stacking context at z-index 2002
          // (BoxSummary.css). Citrus applies the overlay's z-index (700) as an inline
          // style, so on mobile — where the bar is not blurred away — the fixed bar
          // paints on top of this modal. Override the z-index above 2002 (needs
          // `!important` to beat the inline value) so the overlay reliably covers
          // the bar.
          overlayClassName={css`
            position: fixed;
            inset: 0;
            z-index: 2003 !important;
          `}
        >
          {recipeDetail ?? (
            <>
              <ModalHeader>
                <Box display={Display.Flex} paddingH={2}>
                  <Heading3 color={Color.ColdGrey_900}>{activeView.title}</Heading3>
                  <ModalClose onClose={controller.actions.onClose} />
                </Box>
              </ModalHeader>
              <ModalBody>{renderStep(controller)}</ModalBody>
            </>
          )}
        </Modal>
      )}
    </ClassNames>
  )
}

// Memoized so a stable `controller` reference (the hook returns a memoized object)
// lets the modal subtree skip re-rendering when an unrelated parent re-render fires
// without changing any controller value.
export const LoggedOutBoxSummaryModal = React.memo(LoggedOutBoxSummaryModalComponent)

LoggedOutBoxSummaryModal.displayName = 'LoggedOutBoxSummaryModal'
